import DisplayField from '@/components/fields/DisplayField';
import InputField from '@/components/fields/InputField';
import SelectField from '@/components/fields/SelectField';
import { FF_ENABLE_SHOPIFY_PAYMENT } from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import { SectionBody } from '@/shared-ui/PageElements/SectionBody';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import type { AuthData } from '@/types';
import { Alert, AlertTitle, Box, Button, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import type { PaymentButtonText } from '../../ConsultationTab.types';
import { getPaymentTypeText } from '../../utils/getPaymentTypeText';
import type { ShowConsultation } from './ShowConsultation';

export interface RenderPaymentDetailsProps {
  authData: AuthData;
  statusIsNotPaid: boolean;
  paymentsButtonAction: () => Promise<void>;
  isPaymentButtonDisabled: boolean;
  showConsultation: ShowConsultation;
  canShowDiscountRateInput: boolean;
  loadingPaymentAction: boolean;
  consultDiscountRate: string;
  handleChangeConsultDiscountRate: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  formattedConcessionConsulationPrice: string;
  discountInputProps: {
    min: number;
    max: number;
  };
  canUserChargeConsult: boolean;
  handleConcessionPayment: () => Promise<any>;
  loadingChargeConsult: boolean;
  loadingChargeConcession: boolean;
  ffConsultationChargesUplift: boolean;
  getPaymentButtonText(): PaymentButtonText;
}

const RenderPaymentDetails = ({
  authData,
  statusIsNotPaid,
  paymentsButtonAction,
  isPaymentButtonDisabled,
  getPaymentButtonText,
  showConsultation,
  canShowDiscountRateInput,
  loadingPaymentAction,
  consultDiscountRate,
  handleChangeConsultDiscountRate,
  formattedConcessionConsulationPrice,
  discountInputProps,
  canUserChargeConsult,
  handleConcessionPayment,
  loadingChargeConsult,
  loadingChargeConcession,
  ffConsultationChargesUplift,
}: RenderPaymentDetailsProps) => {
  const { isDoc } = authData;
  const { flags } = useFeatureFlags();
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];
  const loadingAnyPayment = loadingPaymentAction || loadingChargeConsult || loadingChargeConcession;

  const canShowChargeConcessionBtn = statusIsNotPaid;

  if (!canUserChargeConsult || isDoc) {
    return null;
  }

  if (ffConsultationChargesUplift) {
    return (
      <Box component="section" mt={4}>
        <SectionHeader headerText="Payment Details" />
        <SectionBody>
          {statusIsNotPaid && (
            <Alert severity={'warning'} sx={{ marginBottom: 4 }}>
              <AlertTitle>Action Required</AlertTitle>
              The patient needs to be charged for the appointment before submit. Thank you for your attention to this
              matter.
            </Alert>
          )}
          <Box flexDirection={'row'} display={'flex'} justifyContent={'space-between'}>
            <Box width={'50%'}>
              <SelectField
                id="discount-type"
                label="Discount Type"
                // TODO: The following options are mocks, source the discount-type status options from BE - https://montugroup.atlassian.net/browse/B2CS-1381
                options={[
                  { name: 'Complimentary Consultation', value: 1 },
                  { name: 'Concession Card', value: 2 },
                  { name: 'Other', value: 3 },
                ]}
                // TODO: We need to save discount-type to BE and set the value below
                // value={''}
                // onChange={() => {}}
                labelPadding={'5px 0px 0px 0px'}
                varient="outlined"
                customGrid={[4, 8]}
              />
            </Box>
            {/* TODO: Only show the following discount-percentage input if the above discount-type input has a selection */}
            <Box width={'25%'}>
              <InputField
                type="number"
                id="discount-percentage"
                label="Discount"
                // TODO: We need to save discount-percentage to BE and set the value below
                // value={0}
                // onChange={() => {}}
                // TODO: Set and disable input at specific values based on discount-type value, unless discount-type value is set to "Other" - https://montugroup.atlassian.net/browse/B2CS-1430
                // disabled={false}
                varient="outlined"
                customGrid={[6, 6]}
              />
            </Box>
            <Button color="primary" size="large" onClick={paymentsButtonAction} disabled={isPaymentButtonDisabled}>
              {getPaymentButtonText()}
            </Button>
          </Box>
        </SectionBody>
      </Box>
    );
  }

  return (
    <Box component="section">
      <SectionHeader headerText="Payment Details" />

      <SectionBody>
        <DisplayField
          id="consultation-payment-details"
          label="Payment Type"
          fieldText={getPaymentTypeText({ consultation: showConsultation, ffEnableShopifyPayment })}
          margin={0}
          labelPadding="0px"
          fieldPadding="0px"
          labelFontWeight={500}
          customGrid={[3, 9]}
        />
        <Box mt={2} display="flex" flexDirection="column">
          <Box display="flex">
            {canShowDiscountRateInput && (
              <Box mr={2}>
                <Tooltip title="Discount rate applied on the consult. A value of 10 represents 10% off.">
                  <TextField
                    label="Discount %"
                    /* Inline style for now until ui cleanup - needs more width for long label */
                    style={{ minWidth: '100px' }}
                    type="number"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      inputProps: discountInputProps,
                    }}
                    value={consultDiscountRate}
                    onChange={handleChangeConsultDiscountRate}
                    disabled={loadingAnyPayment}
                  />
                </Tooltip>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={paymentsButtonAction}
              disabled={isPaymentButtonDisabled}
            >
              {loadingPaymentAction ? <CircularProgress size={16} color="inherit" /> : getPaymentButtonText()}
            </Button>
          </Box>
          {canShowChargeConcessionBtn && (
            <Box>
              <Typography display="block" variant="overline">
                OR
              </Typography>
              <Tooltip title={formattedConcessionConsulationPrice}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConcessionPayment}
                  disabled={loadingAnyPayment}
                >
                  {loadingChargeConcession ? <CircularProgress size={16} color="inherit" /> : 'Charge Concession Fee'}
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      </SectionBody>
    </Box>
  );
};

export default RenderPaymentDetails;
